import { Injectable } from '@angular/core';

export enum PasswordCriteria {
  Length = "Length",
  UpperCaseLetter = "UpperCaseLetter",
  LowerCaseLetter = "LowerCaseLetter",
  Number = "Number",
  SpecialCharacter = "SpecialCharacter",
}

type EnumDictionary<T extends string | symbol | number, U> = {
    [K in T]: U;
};

const symbolsRegex = /[$-/:-?{-~!"^_@`\[\]]/;
const lowerLettersRegex = /[a-z]+/;
const upperLettersRegex = /[A-Z]+/;
const numbersRegex = /[0-9]+/;

@Injectable({
  providedIn: 'root',
})
export class PasswordStrengthService {
  rulesToMatch: EnumDictionary<PasswordCriteria, (password: string) => {}> = {
    [PasswordCriteria.Length]: this.isPasswordLongEnough,
    [PasswordCriteria.UpperCaseLetter]: this.containsUpperCaseLetter,
    [PasswordCriteria.LowerCaseLetter]: this.containsLowerCaseLetter,
    [PasswordCriteria.Number]: this.containsNumber,
    [PasswordCriteria.SpecialCharacter]: this.containsSpecialCharacter,
  };

  public checkRule(ruleType: PasswordCriteria, password: string){
    return this.rulesToMatch[ruleType](password);
  }

  public isPasswordLongEnough(password: string) {
    return password.length >= 8;
  }

  public containsUpperCaseLetter(password: string) {
    return upperLettersRegex.test(password);
  }

  public containsNumber(password: string) {
    return numbersRegex.test(password);
  }

  public containsSpecialCharacter(password: string) {
    return symbolsRegex.test(password);
  }

  public containsLowerCaseLetter(password: string) {
    return lowerLettersRegex.test(password);
  }

  public checkStrength(p: string) {
    let force = 0;
    const lowerLetters = lowerLettersRegex.test(p);
    const upperLetters = upperLettersRegex.test(p);
    const numbers = numbersRegex.test(p);
    const symbols = symbolsRegex.test(p);

    const flags = [lowerLetters, upperLetters, numbers, symbols];

    let passedMatches = 0;
    for (const flag of flags) {
      passedMatches += flag === true ? 1 : 0;
    }

    force += 2 * p.length + (p.length >= 10 ? 1 : 0);
    force += passedMatches * 10;

    force = p.length <= 6 ? Math.min(force, 10) : force;

    force = passedMatches === 1 ? Math.min(force, 10) : force;
    force = passedMatches === 2 ? Math.min(force, 20) : force;
    force = passedMatches === 3 ? Math.min(force, 30) : force;
    force = passedMatches === 4 ? Math.min(force, 40) : force;

    return force;
  }
}
